import React from "react";
import { useParams } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import PageHeader from "../../../components/common/PageHeader";
import ShadowContainer from "../../../components/common/ShadowContainer";
import { getCurrencySymbol } from "../../../helper/formatCurrency";
import { useRestaurantConfig } from "../../../utils/restaurantConfig";
import { API_BASE } from "../../../utils/api";
import { getLocalizedName } from "../../../utils/localizedText";
import "../../../components/Styles/Pages.css";
import {
  BasicInfoSection,
  CategorySection,
  PricingSection,
  ImageSection,
  ActionButtons,
  MultilingualSection,
} from "./components";
import { useProductForm } from "./hooks";
import { PRODUCT_STATUS } from "./utils";

/**
 * Product Add/Edit Page
 * Main component for creating and editing products
 */
function ProductAdd() {
  const { id } = useParams();
  const { locale, features } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);

  const {
    formData,
    setFormData,
    imagePreview,
    categories,
    subcategories,
    status,
    isEditing,
    setIsEditing,
    errors,
    languages,
    enableMultiLang,
    setEnableMultiLang,
    selectedLang,
    setSelectedLang,
    isSubmitting,
    isStatusChanging,
    timingsList,
    addOnsList,
    // hasSubcategory,
    // setHasSubcategory,
    handleCategoryChange,
    handleFileChange,
    handleStatusChange,
    handleSubmit,
  } = useProductForm(id);

  const disabled = status !== PRODUCT_STATUS.OPEN && !isEditing;

  /* =============================
     MULTI LANGUAGE HANDLERS
  ============================== */
  const handleMultiLangFieldChange = (field, lang, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [lang]: value,
      },
    }));
  };

  const handleEnableMultiLangChange = (e) => {
    const checked = e.target.checked;
    setEnableMultiLang(checked);

    if (!checked) {
      setSelectedLang("");
      setFormData((prev) => ({
        ...prev,
        productName: { en: prev.productName.en },
        description: { en: prev.description.en },
      }));
    }
  };

  const handleLangChange = (e) => {
    const lang = e.target.value;
    setSelectedLang(lang);

    setFormData((prev) => ({
      ...prev,
      productName: {
        ...prev.productName,
        [lang]: prev.productName[lang] || "",
      },
      description: {
        ...prev.description,
        [lang]: prev.description[lang] || "",
      },
    }));
  };

  /* =============================
     ✅ SUBCATEGORY HANDLER (FIX)
  ============================== */
  const handleSubcategoryChange = (e) => {
    const value = e.target.value;

    // NO SUBCATEGORY SELECTED
    if (value === "__NO_SUB__") {
      const selectedCategory = categories.find(
        (c) => c._id === formData.category,
      );

      setFormData((prev) => ({
        ...prev,
        subcategory: prev.category, // SAME AS CATEGORY ID
        subcategoryName:
          typeof selectedCategory?.name === "string"
            ? { en: selectedCategory.name }
            : selectedCategory?.name || { en: "" },
      }));
    } else {
      // NORMAL SUBCATEGORY
      setFormData((prev) => ({
        ...prev,
        subcategory: value,
        subcategoryName: "",
      }));
    }
  };

  return (
    <div className="container">
      <PageHeader
        title={
          status === PRODUCT_STATUS.OPEN
            ? "Add Product"
            : status === PRODUCT_STATUS.PENDING
              ? "Verify Product"
              : "Edit Product"
        }
        subtitle={
          <span className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
            {status === PRODUCT_STATUS.OPEN
              ? "Create a new product for your menu"
              : status === PRODUCT_STATUS.PENDING
                ? "Review and verify product details"
                : "Update product information"}
          </span>
        }
        showBackButton={true}
        backTo={-1}
      />

      <ShadowContainer>
        <form className="form">
          <BasicInfoSection
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            disabled={disabled}
          />

          {/* ✅ CATEGORY & SUBCATEGORY */}
          <CategorySection
            formData={formData}
            categories={categories}
            subcategories={subcategories}
            errors={errors}
            disabled={disabled}
            // hasSubcategory={hasSubcategory}        
            // setHasSubcategory={setHasSubcategory}
            onCategoryChange={handleCategoryChange}
            onSubcategoryChange={handleSubcategoryChange}
          />

          {/* TIMINGS (AVAILABLE TIME) */}
          {features?.availableTimeManage !== false && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Available Time (Optional)
              </label>
              <div className="flex flex-wrap gap-3">
                {timingsList && timingsList.map((timing) => {
                   const isSelected = formData.menuTimings?.includes(timing._id);
                   return (
                     <label 
                       key={timing._id} 
                       className={`cursor-pointer px-4 py-2 rounded-full border text-sm font-medium transition-colors ${isSelected ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                     >
                       <input
                         type="checkbox"
                         className="hidden"
                         checked={isSelected || false}
                         disabled={disabled}
                         onChange={() => {
                           const current = formData.menuTimings || [];
                           if (isSelected) {
                              setFormData(prev => ({ ...prev, menuTimings: current.filter(id => id !== timing._id) }));
                           } else {
                              setFormData(prev => ({ ...prev, menuTimings: [...current, timing._id] }));
                           }
                         }}
                       />
                       {timing.name} ({timing.startTime} - {timing.endTime})
                     </label>
                   );
                })}
              </div>
              <p className="mt-1 text-xs text-gray-500">Select specific times this product is available. Leave empty if available all day.</p>
            </div>
          )}

          {/* ADD-ONS */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add-ons (Optional)
            </label>
            {addOnsList && addOnsList.length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {addOnsList.map((addOn) => {
                  const isSelected = formData.addOns?.includes(addOn._id);
                  return (
                    <label
                      key={addOn._id}
                      className={`cursor-pointer flex items-center gap-2 pl-2 pr-4 py-1.5 rounded-full border text-sm font-medium transition-colors ${isSelected ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={isSelected || false}
                        disabled={disabled}
                        onChange={() => {
                          const current = formData.addOns || [];
                          if (isSelected) {
                            setFormData((prev) => ({ ...prev, addOns: current.filter((id) => id !== addOn._id) }));
                          } else {
                            setFormData((prev) => ({ ...prev, addOns: [...current, addOn._id] }));
                          }
                        }}
                      />
                      {addOn.imageUrl && (
                        <img
                          src={`${API_BASE}${addOn.imageUrl}`}
                          alt=""
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      )}
                      {getLocalizedName(addOn.name)} (+{currencySymbol}{Number(addOn.price).toFixed(2)})
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-gray-500">
                No add-ons created yet. Manage them under Product Management &gt; Add-ons.
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">Customers will be able to add these as extras when ordering this product.</p>
          </div>

          {/* DESCRIPTION */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none ${errors.description ? "border-red-500" : "border-gray-300"
                } ${disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white"}`}
              rows={4}
              value={formData.description.en}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: { ...prev.description, en: e.target.value },
                }))
              }
              name="description"
              disabled={disabled}
              placeholder="Enter product description"
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>


          <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
            {/* Vegetarian */}
            <span className="text-sm font-medium text-gray-700">
              Food Type
            </span>

            {[
              { label: "Veg", value: "veg" },
              { label: "Non-Veg", value: "nonveg" },
              { label: "Not Applicable", value: "na" },
            ].map((option) => (
              <label
                key={option.value}
                className="flex items-center space-x-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name="foodType"
                  value={option.value}
                  checked={formData.foodType === option.value}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      foodType: e.target.value,
                    }))
                  }
                  disabled={disabled}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-700">
                  {option.label}
                </span>
              </label>
            ))}

            {/* Include Tax */}
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                checked={!!formData.isTaxable}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    isTaxable: e.target.checked,
                  }))
                }
                disabled={disabled}
              />
              <span className="text-sm font-medium text-gray-700">
                Tax
              </span>
            </label>

            {/* ✅ Online (NEW) */}
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-2 focus:ring-purple-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                checked={!!formData.online}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    online: e.target.checked,
                  }))
                }
                disabled={disabled}
              />
              <span className="text-sm font-medium text-gray-700">
                Online
              </span>
            </label>

            {/* ✅ Today Special */}
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-2 focus:ring-orange-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                checked={!!formData.isTodaysSpecial}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    isTodaysSpecial: e.target.checked,
                  }))
                }
                disabled={disabled}
              />
              <span className="text-sm font-medium text-gray-700">
                Today Special
              </span>
            </label>
          </div>
          <PricingSection
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            disabled={disabled}
            currencySymbol={currencySymbol}
          />

          <ImageSection
            formData={formData}
            imagePreview={imagePreview}
            errors={errors}
            disabled={disabled}
            onFileChange={(e) => {
              if (e.target.type === "file") {
                handleFileChange(e);
              } else if (e.target.name === "isVisible") {
                setFormData((prev) => ({
                  ...prev,
                  isVisible: e.target.checked,
                }));
              }
            }}
          />

          <MultilingualSection
            enableMultiLang={enableMultiLang}
            selectedLang={selectedLang}
            languages={languages}
            formData={formData}
            disabled={disabled}
            onEnableChange={handleEnableMultiLangChange}
            onLangChange={handleLangChange}
            onFieldChange={handleMultiLangFieldChange}
          />

          <ActionButtons
            status={status}
            isEditing={isEditing}
            onSubmit={handleSubmit}
            onStatusChange={handleStatusChange}
            onEdit={() => setIsEditing(true)}
            isSubmitting={isSubmitting}
            isStatusChanging={isStatusChanging}
          />
        </form>
      </ShadowContainer>
    </div>
  );
}

export default ProductAdd;
